/**
 * A governor's brief: seven numbers, and what the world has published.
 *
 * `IMPLEMENTATION-GOVERNORS.md` §6 built the whole of Phase 2 against *"a **stub
 * polity** — a scripted local governor reading notices, tithes and priority lists
 * from a config file"*, and `docs/roadmap.md`'s second Phase 3 item asks for that
 * config file to become a *resident* governor: one that ships in the client, plays
 * a district through the ordinary vocabulary, and gives the author a governed world
 * to look at before there is a server to hold one.
 *
 * This is the config file. It is deliberately small, and everything in it is either
 * a bound on what the polity will ask for or a fact about what the world has done.
 * There is no strategy in here and there is no personality: §13's governors are
 * *"an inbox, a ledger, and a map of maps"*, and this build has no inbox, so what is
 * left is the ledger arithmetic. `ResidentGovernor` is the rule that reads these
 * numbers; this file is only the numbers.
 *
 * ## Why an appetite rather than a treasury
 *
 * §8 gives the polity a treasury — facility profits and tithes in, coupons and
 * construction out — and the treasury is what makes a governor desperate. It does
 * not exist (Q63: the operating profit is measured, recorded, and received by
 * nobody), and building it is roadmap item 9, on the far side of the server.
 *
 * `REVENUE_TARGET` is what stands in for it: **dollars the polity wants out of this
 * district per season**, against which the two figures the Chronicle already
 * publishes — what the estate made here, and what was remitted — are compared. That
 * is one line of a treasury rather than a treasury, and it is enough to make the
 * dial move for a measured reason instead of on a schedule (Law 1). What it cannot
 * do is *compound*: a polity that misses a coupon is a polity whose appetite grows,
 * and this appetite is a constant. The pump has a piston and no ratchet yet.
 *
 * ## What is not in here, and why
 *
 * - **No power or water allocation.** §18 gives a governor `set_power_priority` and
 *   `set_water_priority`, and Q61 has already settled what they will be: generation
 *   moves to the polity and the priority list becomes the source of a district's
 *   allocations. Until it does, a polity has no generation — so a brief that quoted
 *   a district 300 kW would be allocating megawatts nobody produced, which is an
 *   authored delta wearing a dial's clothes. The allocation setters stay what they
 *   have been since ADR-013: a scripted call (`sim.setPowerAllocation`), used by the
 *   scenario harness's vignettes and reachable from the client's console.
 * - **No weather.** Rainfall is physics and the settlement pipeline's `physics`
 *   stage says why it does not set it (`SeasonManager`): weather is published for
 *   everybody at once, and a district that rolled its own rain would have a drought
 *   no governor could have forecast. A resident governor is not everybody.
 * - **No words.** §13 calls the writing *"most of the game"*. There is no mail in
 *   this build, so the resident governor is two-thirds of a governor: it reads and
 *   it decides, and the only sentence it ever says to a district is the tithe line
 *   `SurfaceSim.setTithe` puts in the news ticker.
 */

import { FacilityTypeName } from '@antification/protocol';

/**
 * A price the world posts at the opening of one season.
 *
 * §5's World Market is *"the boundary condition standing in for the rest of the
 * world"*, and §5 puts its schedule, its noise and its regime shifts in the polity
 * tier — which is why `MarketManager` has none of them and takes what it is told
 * (ADR-025). A schedule is the smallest thing that can stand in for the tier: a
 * list of seasons and what compute fetched in each of them, authored rather than
 * generated, because Law 3 forbids dice in politics and there is nowhere honest to
 * put dice in physics yet either.
 *
 * An omitted field is left alone, exactly as `SurfaceSim.postMarket` leaves it: a
 * market that repriced compute has not repriced debt.
 */
export interface PricePosting {
  /** The season this posting opens. 1 is a district's first. */
  season: number;
  /** Dollars a compute-unit fetches. Ships at $60 (`DEFAULT_MARKET_CONFIG`). */
  computePrice?: number;
  /**
   * Dollars a goods-unit costs. Ships at $300.
   *
   * §5's *"a goods recession"* is this field and a season number. It is the one
   * posted price in this build that moves against the **polity** rather than
   * against the district — construction buys goods, so a repriced world makes
   * somebody's building site cost more halfway through building it, and the
   * governor's own appetite arithmetic is what notices.
   */
  goodsPrice?: number;
  /** What a dollar of district debt costs per budget cycle. Ships at 0.0005. */
  bondYield?: number;
}

/** No schedule: what an unquoted district trades at, forever. */
export const NO_POSTINGS: readonly PricePosting[] = Object.freeze([]);

/**
 * The dials of a brief — the part that is somebody's instructions.
 *
 * Separate from the schedule above it because the two are different kinds of thing
 * and the split has to survive the server: these are what a polity decided, and the
 * schedule is what the world announced. A `Partial<GovernorDials>` is also exactly
 * what a settings panel can edit, which is why every field here is a number.
 */
export interface GovernorDials {
  /**
   * Dollars the polity wants out of this district per season.
   *
   * Compared against what the Chronicle says it actually got: the estate's
   * operating profit made here, plus the tithe remitted. See the header on why this
   * is an appetite rather than a treasury.
   *
   * $20,000 is chosen against the *shape* §8 asserts and not against an outcome:
   *
   * > **Prosperity:** facility profits dwarf tithe income; rational governors set
   * > tithes near zero as cheap goodwill. […] **Crisis:** facility income
   * > collapses […] The tithe is the lever within reach.
   *
   * For that shape to appear, the target has to be *unreachable* from a district's
   * own tax base and *comfortably covered* by one running facility. A plain paved
   * district collects $12,000–17,500 of tax a season, so even the Charter's whole
   * 40% cap remits under $7,000 — the target cannot be met by tithing, and a
   * district with nothing on it is therefore tithed at the ceiling. One data centre
   * at full uptime makes $72,000 of operating profit (`docs/world-market.md`), which
   * is three and a half times the target — so hosting one takes the dial to the
   * floor, and §8's *"cheap goodwill"* is arithmetic rather than a courtesy.
   *
   * Read as a polity: three districts at this target want $60,000 a season, which is
   * a little under one data centre's margin. That is the number this is, and nothing
   * in the build says whether it is a reasonable one.
   */
  REVENUE_TARGET: number;
  /**
   * The lowest rate this governor will levy. Negative is a grant (§18's `grant`).
   *
   * At the shipped 0, a district that pays its way is left alone. §9's *Long Summer*
   * — *"Tithes to zero, then a season of negative tithe — grants flow down"* — is
   * this dial at −0.15, and the rule above it needs no new branch to produce it: a
   * polity whose appetite is already satisfied asks for the floor, whatever the
   * floor is.
   */
  TITHE_FLOOR: number;
  /**
   * The highest rate this governor will levy, as a share of district tax revenue.
   *
   * 0.25 rather than the Charter's 0.4 cap (`TITHE_CAP`), and the gap is the point:
   * a ceiling *at* the cap would make every levy look like the maximum the law
   * allows, and this way the number on the panel is visibly somebody's choice.
   * §9's crisis figure is 22%, which is what this is rounded from.
   *
   * A brief whose ceiling exceeds the Charter's cap is a brief whose top rate is
   * refused rather than clamped (`FiscalManager.setTitheRate`) — the command is
   * well-formed, does nothing, and the district stays on the rate it was on. The
   * rule does not check the cap for the same reason the command layer does not: the
   * cap is a property of the world the command lands in, and this is Phase 7's
   * Charter arriving early enough to break something.
   */
  TITHE_CEILING: number;
  /**
   * Truck-legs per budget cycle routed through this district's corridor.
   *
   * Offered only once the district has two interchanges, which is the whole
   * condition §5 puts on a corridor and the one measured fact the rule consults
   * before spending the polity's trucks. A district with one interchange and a
   * generous polity would get a lapsed-leg count and no traffic, which is honest
   * and useless; a governor that can read a map does not do it.
   *
   * One leg a cycle is the middle of `docs/freight.md`'s measured ladder: 155
   * crossings and $2,184 of toll over two seasons, against half a point of road wear
   * each. Two of those figures are the district's and the third is the reason the
   * trucks exist, and nothing in this brief connects them.
   */
  FREIGHT_LEGS: number;
  /**
   * How many facilities this governor will put on this district, ever.
   *
   * A bound, not a plan: the rule sites one when the district has failed to meet the
   * target, and one running data centre meets it three and a half times over — so
   * what usually stops the estate at one is the arithmetic rather than this number.
   * It binds in the case that matters, which is a district whose estate has stopped
   * producing: profit falls, the target is missed, and the rule reaches for another
   * rectangle.
   *
   * Two, because `docs/world-market.md` measured what the third one does: at three
   * data centres the federal load alone (540 kW) exceeds the district's whole
   * interconnect, facilities are last in the shed order, and all thirty-three
   * families leave in one season with `brownout` as the recorded cause. **Nothing in
   * this brief knows that.** The limit is two because somebody wrote two, and a
   * governor whose brief said three would empty the district it was extracting from
   * without a single line of its instructions being about a district.
   *
   * Since ADR-029 that trap takes considerably longer to spring and in a poor
   * district never springs at all, because each rectangle is two seasons and 240
   * lorries away from drawing its first kilowatt. The limit is still two, the trap
   * is still there, and what construction bought is delay paid for in the host's
   * roads (`docs/governor.md`).
   */
  ESTATE_LIMIT: number;
  /**
   * The first season this governor will seize ground in.
   *
   * Two: a season is watched before it is acted on. There is nothing principled
   * about the number — a governor could seize on day one — but a district that is
   * force-placed before it has a Chronicle line is a district whose seizure was
   * decided by a constant rather than by a measurement, and the whole point of this
   * class is that its moves are answers to the record.
   */
  ESTATE_FROM_SEASON: number;
  /**
   * Seasons between seizures. Two.
   *
   * §7's protocol has deadlines and response windows and this has neither, so the
   * interval is standing in for the whole of the procedural friction a real siting
   * carries: a notice, offers, objections, a counter, an award. When the Siting
   * Protocol arrives (roadmap item 8), the clock that spaces facilities out is the
   * protocol's deadlines and this number goes away.
   */
  ESTATE_INTERVAL: number;
}

/** What a brief is: instructions, and what the world published. */
export interface GovernorBrief extends GovernorDials {
  /** The world's half. Empty ships the price of last resort, forever. */
  readonly PRICES: readonly PricePosting[];
}

/**
 * The brief the client ships with.
 *
 * Chosen so that **attaching this governor to a fresh district changes nothing
 * until a season closes**: no price is posted, no allocation is touched, no ground
 * is seizable before season 2, and the tithe cannot be computed until there is a
 * tax base to take a share of. That property is ADR-017 D105's *absent, not zero*
 * applied to a whole actor — a world with a governor in it and nothing measured yet
 * is bit-identical to a world with no governor — and `governor.test.ts` asserts it
 * by hash rather than by argument.
 */
export const DEFAULT_GOVERNOR_BRIEF: GovernorBrief = {
  REVENUE_TARGET: 20_000,
  TITHE_FLOOR: 0,
  TITHE_CEILING: 0.25,
  FREIGHT_LEGS: 1,
  ESTATE_LIMIT: 2,
  ESTATE_FROM_SEASON: 2,
  ESTATE_INTERVAL: 2,
  PRICES: NO_POSTINGS,
};

/**
 * What this governor sites when it sites something.
 *
 * The data centre, because it is the only facility type any simulation in this
 * repository can put on a map (`FACILITY_TYPE_NAMES` has one member, and the
 * protocol says why). A brief field for it would be a choice with one option.
 */
export const BRIEF_FACILITY: FacilityTypeName = FacilityTypeName.DATA_CENTER;
